import * as React from 'react';
import { useState } from 'react';

// Interactive page overlays ("hotspots").
//
// Every interactive thing on a flipbook page — links baked into the PDF today,
// user-placed links / videos / images later (Flipsnack-style) — is one shape:
//
//   { id, page, x, y, w, h, type, ...payload }
//
// x/y/w/h are fractions (0..1) of the page box, so a hotspot survives any page
// size or zoom, and the same data can drive an editor later. Each hotspot is
// rendered inside its page's DOM, so it turns with the page during a flip.
//
// To add a new kind, add a renderer to RENDERERS. Unknown types are skipped, so
// older viewers ignore hotspots they don't understand.

const stop = (e) => e.stopPropagation();

const boxStyle = (h) => ({
    position: 'absolute',
    left: `${h.x * 100}%`,
    top: `${h.y * 100}%`,
    width: `${h.w * 100}%`,
    height: `${h.h * 100}%`,
});

// External URL. { type: 'link', url }
const LinkHotspot = ({ hotspot }) => (
    <a
        href={hotspot.url}
        target="_blank"
        rel="noopener noreferrer nofollow"
        title={hotspot.title || hotspot.url}
        className="fb-hotspot fb-hotspot-link"
        style={boxStyle(hotspot)}
        onPointerDown={stop}
        onClick={stop}
    />
);

// Jump within the document. { type: 'page', targetPage }
const PageHotspot = ({ hotspot, onGoToPage }) => (
    <button
        type="button"
        title={hotspot.title || `Go to page ${hotspot.targetPage}`}
        className="fb-hotspot fb-hotspot-link"
        style={boxStyle(hotspot)}
        onPointerDown={stop}
        onClick={(e) => {
            stop(e);
            onGoToPage(hotspot.targetPage);
        }}
    />
);

const embedUrl = (src) => {
    const yt = src.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{6,})/);
    if (yt) return `https://www.youtube-nocookie.com/embed/${yt[1]}?autoplay=1&rel=0`;
    const vimeo = src.match(/vimeo\.com\/(?:video\/)?(\d+)/);
    if (vimeo) return `https://player.vimeo.com/video/${vimeo[1]}?autoplay=1`;
    return null;
};

// Video placed on the page. { type: 'video', src, poster? }
// Shows a poster + play button until clicked, so a page full of videos costs
// nothing until someone actually plays one.
const VideoHotspot = ({ hotspot }) => {
    const [playing, setPlaying] = useState(false);
    const embed = embedUrl(hotspot.src);
    return (
        <div className="fb-hotspot fb-hotspot-video" style={boxStyle(hotspot)} onPointerDown={stop} onClick={stop}>
            {playing ? (
                embed ? (
                    <iframe
                        src={embed}
                        title={hotspot.title || 'Video'}
                        allow="autoplay; fullscreen; picture-in-picture"
                        allowFullScreen
                        style={{ width: '100%', height: '100%', border: 0 }}
                    />
                ) : (
                    <video src={hotspot.src} poster={hotspot.poster} controls autoPlay style={{ width: '100%', height: '100%', background: '#000' }} />
                )
            ) : (
                <button
                    type="button"
                    className="fb-video-poster"
                    aria-label="Play video"
                    onClick={() => setPlaying(true)}
                    style={hotspot.poster ? { backgroundImage: `url(${hotspot.poster})` } : undefined}
                >
                    <span className="fb-video-play" />
                </button>
            )}
        </div>
    );
};

const RENDERERS = {
    link: LinkHotspot,
    page: PageHotspot,
    video: VideoHotspot,
};

export const PageOverlays = React.memo(({ hotspots, onGoToPage }) => {
    if (!hotspots || !hotspots.length) return null;
    return (
        <div className="fb-overlays">
            {hotspots.map((h, i) => {
                const Renderer = RENDERERS[h.type];
                return Renderer ? <Renderer key={h.id || i} hotspot={h} onGoToPage={onGoToPage} /> : null;
            })}
        </div>
    );
});

// Turn a PDF page's own link annotations into hotspots, so links authored in
// the source document work in the flipbook through the same overlay layer.
export const extractPdfLinks = async (pdf, pageNumber) => {
    const page = await pdf.getPage(pageNumber);
    const viewport = page.getViewport({ scale: 1 });
    const annotations = await page.getAnnotations({ intent: 'display' });
    const out = [];
    for (const a of annotations) {
        if (a.subtype !== 'Link' || !a.rect) continue;
        const [x1, y1, x2, y2] = viewport.convertToViewportRectangle(a.rect);
        const box = {
            x: Math.min(x1, x2) / viewport.width,
            y: Math.min(y1, y2) / viewport.height,
            w: Math.abs(x2 - x1) / viewport.width,
            h: Math.abs(y2 - y1) / viewport.height,
        };
        const id = `pdf-${pageNumber}-${a.id}`;
        if (a.url) {
            out.push({ id, page: pageNumber, type: 'link', url: a.url, ...box });
        } else if (a.dest) {
            try {
                const dest = typeof a.dest === 'string' ? await pdf.getDestination(a.dest) : a.dest;
                if (dest && dest[0]) {
                    const index = await pdf.getPageIndex(dest[0]);
                    out.push({ id, page: pageNumber, type: 'page', targetPage: index + 1, ...box });
                }
            } catch (e) {
                // Broken internal destination — skip it rather than fail the page.
            }
        }
    }
    return out;
};
